import * as React from 'react';

import { useIntl } from 'react-intl';

import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useGetUserActionEvent } from 'src/features/userAction/api/useGetUserActionEvent';
import CloudUserBadge from 'src/features/userAction/components/CloudUserBadge';
import UserActionMessage from 'src/features/userAction/components/UserActionMessage';
import { UpdateEvent, userActionBadgeOptions, EventData } from 'src/features/userAction/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';

type Props = {
    eventId: string;
};
export default function UserActionSidePanel({ eventId }: Props) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { data } = useGetUserActionEvent({ eventId, parkingId, config: { enabled: !!eventId } });

    const badgeOption = React.useMemo(() => (data ? userActionBadgeOptions[data.action_type] : data), [data]);

    const attributes = React.useMemo(
        () => [
            {
                label: formatMessage(statisticMessages.createdAt),
                value: getFormattedDateTimeForDetailPanel(data?.timestamp),
            },
            {
                label: formatMessage(generalMessages.name),
                value: data ? (
                    <div className="flex flex-row gap-1">
                        <div>{data.name}</div>
                        <div>{!data.user && <CloudUserBadge />}</div>
                    </div>
                ) : (
                    data
                ),
            },
            {
                label: formatMessage(statisticMessages.email),
                value: data?.email,
            },
            {
                label: formatMessage(statisticMessages.message),
                value: data ? <UserActionMessage userAction={data} /> : data,
            },
        ],
        [data, formatMessage],
    );

    const changes = React.useMemo(() => {
        const eventData = data?.event_data as UpdateEvent<EventData>;
        const changes = eventData?.changes ? Object.entries(eventData.changes) : [];
        return changes.map(([label, { from_value, to_value }]) => ({
            label,
            from_value,
            to_value,
        }));
    }, [data]);

    return (
        <>
            <DialogHeader>
                {data ? (
                    <>
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(statisticMessages.userAction)}
                        </DialogTitle>
                        <DialogDescription>
                            <Badge theme={badgeOption?.theme}>
                                {badgeOption?.label ? formatMessage(badgeOption.label) : data.action_type}
                            </Badge>
                        </DialogDescription>
                    </>
                ) : (
                    <>
                        <Skeleton className="h-7 w-32" />
                        <Skeleton className="h-5 w-60" />
                    </>
                )}
            </DialogHeader>
            <DialogSeparator />
            <DialogBody>
                {attributes.map(({ label, value }) => (
                    <SidePanelBodyAttribute key={label}>
                        <SidePanelBodyAttributeTitle>{label}</SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control={value} fallbackMessage="-">
                            {value}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                ))}

                {changes.length > 0 && (
                    <SidePanelBodyAttributeTitle className="text-base text-black">
                        {formatMessage(statisticMessages.changes)}
                    </SidePanelBodyAttributeTitle>
                )}
                {changes.map(({ label, from_value, to_value }) => (
                    <SidePanelBodyAttribute key={label}>
                        <SidePanelBodyAttributeTitle>{label}</SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={from_value}
                            fallbackMessage={`${formatMessage(statisticMessages.fromValue)}: -`}
                        >
                            {formatMessage(statisticMessages.fromValue)}: {from_value}
                        </SidePanelBodyAttributeBody>
                        <SidePanelBodyAttributeBody
                            control={to_value}
                            fallbackMessage={`${formatMessage(statisticMessages.toValue)}: -`}
                        >
                            {formatMessage(statisticMessages.toValue)}: {to_value}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                ))}
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
